<template>
  <transition name="fade">
    <div v-if="open"
         class="popover-modal"
         :class="{ 'pdf-modal': hideHeader }">
      <div class="modal-shadow" @click="requestClose"/>
      <div class="w-full h-full bg-white rounded-sm">
        <template v-if="shouldShowHeader">
        <header class="flex h-base items-center pl-base">
          <slot name="header" />
          <button class="w-base h-base ml-auto" @click="requestClose">
            <i class="material-icons">close</i>
          </button>
        </header>
        </template>
        <slot/>
      </div>
    </div>
  </transition>
</template>

<script>
import { bodyScrollGuard } from '../../../lib/control-body-scroll'
import clickOutside from 'vue-click-outside'

export default {
  name: 'PopoverModal',
  directives: { clickOutside },
  props: {
    open: { type: Boolean, required: true },
    hideHeader: { type: Boolean, default: false }
  },

  data: () => ({
    allowClickOutside: true
  }),

  computed: {
    shouldShowHeader () {
      return !this.hideHeader
    }
  },

  created () {
    window.addEventListener('keydown', e => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.$emit('esc', e)
      }
    })
  },

  mounted () {
    this.setBodyScroll(this.open)
    this.setAllowClickOutside()
    // Force remove header if hideHeader is true
    if (this.hideHeader) {
      this.$nextTick(() => {
        if (this.$el && this.$el.nodeType === 1) {
          const header = this.$el.querySelector('header')
          if (header) {
            header.remove()
          }
          // Also check in nested elements
          const nestedHeader = this.$el.querySelector('.w-full header')
          if (nestedHeader) {
            nestedHeader.remove()
          }
        }
      })
    }
    // Debug: check if hideHeader prop is being received
    if (this.$el && this.$el.nodeType === 1 && this.$el.classList && this.$el.classList.contains('pdf-modal')) {
      console.log('PopoverModal mounted with hideHeader:', this.hideHeader, 'shouldShowHeader:', this.shouldShowHeader)
    }
  },

  watch: {
    open (value) {
      this.setBodyScroll(value)
      this.setAllowClickOutside()
    },
    hideHeader (value) {
      // Force remove header if hideHeader becomes true
      if (value) {
        this.$nextTick(() => {
          if (this.$el && this.$el.nodeType === 1) {
            const header = this.$el.querySelector('header')
            if (header) {
              header.remove()
            }
          }
        })
      }
    }
  },

  methods: {
    requestClose () {
      if (this.open && this.allowClickOutside) {
        this.$emit('close')
      }
    },

    setBodyScroll (freeze) {
      // On mobile, don't freeze body scroll for PDF modals - let the modal handle its own scrolling
      if (window.innerWidth <= 480 && this.$el && this.$el.classList && this.$el.classList.contains('pdf-modal')) {
        // Don't freeze body scroll on mobile for PDF modals
        return
      }
      if (freeze) {
        bodyScrollGuard.freezeBodyScroll()
      } else {
        bodyScrollGuard.releaseBodyScroll()
      }
    },

    releaseBgScroll () {
      this.setBodyScroll(false)
    },

    setAllowClickOutside () {
      if (this.open) {
        setTimeout(() => { this.allowClickOutside = true }, 100)
      } else {
        this.allowClickOutside = false
      }
    }
  },

  beforeDestroy () {
    bodyScrollGuard.releaseBodyScroll()
  }
}
</script>

<!--suppress CssInvalidAtRule -->
<style lang="scss">
  // @import "../../../assets/styles/vars";
  // @import "../../../assets/styles/mixins";
  $basic-size: 3.8rem;
  $basic-padding: 1rem;
  $content-min-width: 5rem;
  $tag-inline-height: 3.8rem;

  .popover-modal {
    position: fixed;
    z-index: 9999;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 100vw;
    max-height: 100vh;

    @media (max-width: 30rem) {
      width: 100vw !important;
      max-width: 100vw !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      transform: none !important;
    }

    .modal-shadow {
      content: '';
      position: fixed;
      height: calc(100vh + 10px);
      width: calc(100vw + 10px);
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.5);
      z-index: -1;

      @media (max-width: 30rem) {
        width: 100vw !important;
        height: 100vh !important;
        left: 0 !important;
        top: 0 !important;
        transform: none !important;
      }
    }

    .modal-content-box {
      position: relative;
      width: 100%;
      max-width: min-content;
    }

    // PDF modal specific mobile styles
    &.pdf-modal {
      @media (max-width: 30rem) {
        height: 100vh !important;
        max-height: 100vh !important;
        overflow: visible !important;

        .w-full {
          height: 100vh !important;
          max-height: 100vh !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          touch-action: pan-y !important;
          pointer-events: auto !important;
          display: flex !important;
          flex-direction: column !important;
          position: relative !important;
          // Ensure it can scroll
          overscroll-behavior: contain !important;
          will-change: scroll-position !important;
        }

        // Also ensure pdf-preview-container can scroll
        .pdf-preview-container {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          touch-action: pan-y !important;
          pointer-events: auto !important;
          height: 100% !important;
          max-height: 100% !important;
        }

        // Ensure pdf-wrapper can scroll
        .pdf-wrapper {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          touch-action: pan-y !important;
          pointer-events: auto !important;
        }
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s;
  }

  /* .fade-leave-active below version 2.1.8 */
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  // Disable fade transition for PDF modals
  .pdf-modal.fade-enter-active,
  .pdf-modal.fade-leave-active {
    transition: none !important;
  }

  .pdf-modal.fade-enter,
  .pdf-modal.fade-leave-to {
    opacity: 1 !important;
  }

  // Hide header and its close button for PDF modals - safety CSS in case v-if doesn't work
  .pdf-modal header,
  .pdf-modal header *,
  .pdf-modal header button,
  .pdf-modal header .w-base,
  .pdf-modal header .h-base,
  .pdf-modal header .ml-auto,
  .pdf-modal .w-full header,
  .pdf-modal .w-full header *,
  .pdf-modal .w-full header button,
  .pdf-modal .w-full header .w-base,
  .pdf-modal .w-full header .h-base,
  .pdf-modal .w-full header .ml-auto,
  .popover-modal.pdf-modal header,
  .popover-modal.pdf-modal header *,
  .popover-modal.pdf-modal header button {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    height: 0 !important;
    width: 0 !important;
    min-height: 0 !important;
    max-height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    pointer-events: none !important;
    position: absolute !important;
    top: -9999px !important;
    left: -9999px !important;
    overflow: hidden !important;
  }
</style>
